import { useCallback, useEffect, useSyncExternalStore } from 'react';

export type ThemePref = 'sistema' | 'claro' | 'oscuro';

const KEY = 'nutrigym:tema';

/**
 * Estado a nivel de modulo, no por componente: el tema se lee desde App y
 * desde Perfil a la vez, y dos `useState` independientes acabarian peleandose
 * por la clase del <html>.
 */
let current: ThemePref = readStored();
const listeners = new Set<() => void>();

function readStored(): ThemePref {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === 'claro' || raw === 'oscuro' || raw === 'sistema') return raw;
  } catch {
    // Safari en navegacion privada puede lanzar al leer. Se cae al sistema.
  }
  return 'sistema';
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ThemePref {
  return current;
}

export function applyTheme(pref: ThemePref = current): void {
  if (typeof document === 'undefined') return;
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = pref === 'oscuro' || (pref === 'sistema' && prefersDark);
  document.documentElement.classList.toggle('dark', dark);
}

export function setThemePref(next: ThemePref): void {
  current = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Sin persistencia el tema dura la sesion. Aceptable.
  }
  applyTheme(next);
  for (const listener of listeners) listener();
}

export function useTheme(): { pref: ThemePref; setPref: (p: ThemePref) => void } {
  const pref = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    applyTheme(pref);
    if (pref !== 'sistema') return;
    // Con 'sistema' hay que seguir al SO en caliente.
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('sistema');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  const setPref = useCallback((next: ThemePref) => setThemePref(next), []);
  return { pref, setPref };
}
